import type { ReviewSuggestion } from '../../types';
import type { ResolvedReviewSuggestionRange } from './types';

export function getActiveReviewSuggestions(
  reviewSuggestions: ReviewSuggestion[] | undefined,
  resolvedSuggestionIds: ReadonlySet<string>,
): ReviewSuggestion[] {
  return reviewSuggestions?.filter((suggestion) => !resolvedSuggestionIds.has(suggestion.id)) ?? [];
}

export function orderReviewSuggestionsByResolvedRanges(
  reviewSuggestions: ReviewSuggestion[] | undefined,
  resolvedSuggestionIds: ReadonlySet<string>,
  ranges: readonly ResolvedReviewSuggestionRange[] | undefined,
): ReviewSuggestion[] {
  const activeSuggestions = getActiveReviewSuggestions(reviewSuggestions, resolvedSuggestionIds);
  if (!ranges) return activeSuggestions;
  if (ranges.length === 0) return [];

  const byId = new Map(activeSuggestions.map((suggestion, index) => [suggestion.id, { suggestion, index }]));
  const orderedSuggestions = ranges
    .map((range, index) => ({ range, index }))
    .sort((a, b) => {
      if (a.range.from !== b.range.from) return a.range.from - b.range.from;
      if (a.range.to !== b.range.to) return a.range.to - b.range.to;

      return a.index - b.index;
    })
    .map(({ range }) => byId.get(range.id)?.suggestion)
    .filter((suggestion): suggestion is ReviewSuggestion => Boolean(suggestion));

  return orderedSuggestions;
}

export function getNextReviewSuggestionId(reviewSuggestions: ReviewSuggestion[] | undefined, currentSuggestionId: string): string | null {
  const currentIndex = reviewSuggestions?.findIndex((suggestion) => suggestion.id === currentSuggestionId) ?? -1;
  if (currentIndex === -1) return null;

  return reviewSuggestions?.[currentIndex + 1]?.id ?? null;
}
